import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, AlertTriangle, RefreshCw, Plug, Unplug } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errMsg } from "./mk";
import MetaLiveTest from "./MetaLiveTest";

const EMPTY = { app_id: "", app_secret: "", user_token: "", page_id: "418240964961350", ig_id: "17841435919257280" };

const ConnectForm = ({ status, onDone, onCancel }) => {
  const [f, setF] = useState({ ...EMPTY, app_id: "", page_id: status?.page_id || EMPTY.page_id, ig_id: status?.ig_id || EMPTY.ig_id });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing/meta/connect", f);
      setF(EMPTY);
      toast.success("Connected to Meta");
      onDone(data.status);
    } catch (err) { toast.error(errMsg(err), { duration: 9000 }); }
    finally { setBusy(false); setF((p) => ({ ...p, user_token: "" })); }
  };
  const field = (k, label, type = "text", hint) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={f[k]} onChange={set(k)} autoComplete="off" spellCheck={false} data-testid={`meta-${k.replace("_", "-")}`} />
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-blue-100 bg-blue-50/40 p-3" data-testid="meta-connect-form">
      {field("app_id", "Meta App ID", "text", "Meta for Developers → PTG AI Marketing → App settings → Basic")}
      {field("app_secret", "App Secret", "password", status?.connected ? "Leave blank to keep the saved secret" : "Same page — click Show, then copy")}
      {field("user_token", "User token (from Graph API Explorer)", "password", "Used once to create a long-lasting Page token, then discarded")}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {field("page_id", "Facebook Page ID")}
        {field("ig_id", "Instagram account ID")}
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} className="bg-blue-900 hover:bg-blue-800" data-testid="meta-connect-submit">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plug className="mr-1 h-4 w-4" />} Check & connect
        </Button>
        {onCancel && <Button type="button" variant="ghost" onClick={onCancel} data-testid="meta-connect-cancel">Cancel</Button>}
      </div>
      <p className="text-[11px] text-slate-500">Saved encrypted on the server. Secrets are never shown again. Live posting stays OFF after connecting.</p>
    </form>
  );
};

const Health = ({ s }) => {
  const c = s.last_check;
  if (!c) return null;
  return (
    <div className={`rounded-xl p-3 text-xs ${c.ok ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-800"}`} data-testid="meta-health">
      <div className="flex items-center gap-1 font-bold">{c.ok ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        {c.ok ? "Connection healthy" : "Connection problem"} · checked {new Date(c.at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST</div>
      {(c.problems || []).map((p) => <div key={p}>• {p}</div>)}
      {(c.warnings || []).map((w) => <div key={w} className="text-amber-800">• {w}</div>)}
      <div className="mt-1 text-slate-600">Page token: {c.expires_at ? `expires ${c.expires_at.slice(0, 10)}` : "no fixed expiry (can still be revoked by Meta)"}
        {c.data_access_expires_at ? ` · data access until ${c.data_access_expires_at.slice(0, 10)}` : ""}</div>
    </div>
  );
};

export default function MetaConnect({ onChanged }) {
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/marketing/meta").then(({ data }) => setS(data)).catch(() => setS({ connected: false }));
  useEffect(() => { load(); }, []);
  const update = (data) => { setS(data); setEditing(false); onChanged?.(); };
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const { data } = await fn(); if (ok) toast.success(ok); update(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  if (!s.connected || editing) return <ConnectForm status={s} onDone={update} onCancel={s.connected ? () => setEditing(false) : null} />;
  return (
    <div className="space-y-3" data-testid="meta-connected">
      <div className="text-sm text-slate-700">
        <div><b>{s.page_name}</b> (Page {s.page_id}) · Instagram <b>@{s.ig_username}</b></div>
        <div className="text-xs text-slate-500">App {s.app_id_hint} · connected by {s.connected_by}</div>
      </div>
      <Health s={s} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => run("check", () => api.post("/admin/marketing/meta/check"), "Checked")} data-testid="meta-check-btn">
          {busy === "check" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} Check connection
        </Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="meta-reconnect-btn"><Plug className="mr-1 h-4 w-4" /> Reconnect</Button>
        <Button size="sm" variant="ghost" className="text-red-600" disabled={!!busy} data-testid="meta-disconnect-btn"
          onClick={() => window.confirm("Disconnect Meta? Saved credentials will be deleted and posting stops.") && run("dis", async () => { await api.delete("/admin/marketing/meta"); return { data: { connected: false } }; }, "Disconnected")}>
          <Unplug className="mr-1 h-4 w-4" /> Disconnect
        </Button>
      </div>
      <MetaLiveTest s={s} onUpdate={update} />
    </div>
  );
}
